/**
 * @file: useStoreConfiguration.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAuthContext, useCurrentUser } from '@euler/app/flows/auth/context';
import { kAppLaunchConfigKey } from '@euler/functions/constants';
import {
  clearAsyncStorageSubjectForKey,
  useAsyncStorage,
} from '@euler/functions/useAsyncStorage';
import {
  AppLaunchConfig,
  StoreConfiguration,
  StoreConfigurationWithHash,
} from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useEffect } from 'react';
import { noop } from 'rxjs';

export function useStoreConfiguration(): StoreConfiguration {
  const user = useCurrentUser();
  const { storeService } = useServiceFactory();
  const storeId = user?.store.id ?? 0;

  const [conf, setConfig] = useAsyncStorage<StoreConfigurationWithHash>(
    `store.${storeId}.config`,
    undefined,
    { storageType: 'file' },
  );

  const loadedFromCache = conf !== undefined;

  const fetch = usePersistFn(() => {
    void storeService
      .getConfig()
      .then(res => {
        if (res.__hash !== conf?.__hash) {
          __DEV__ &&
            console.log(
              'local store configuration updated with latest from server',
            );
          setConfig(res);
        }
      })
      .catch(onErrorIgnore);
  });

  useEffect(() => {
    if (loadedFromCache) {
      fetch();
    }
  }, [fetch, loadedFromCache]);

  useEffect(
    () => () => {
      if (!storeId) {
        clearAsyncStorageSubjectForKey(`store.${storeId}.config`);
      }
    },
    [storeId],
  );
  return conf ?? { version: '1.0' };
}

export function useAppLaunchConfig(): AppLaunchConfig | null | undefined {
  const { storeService } = useServiceFactory();
  const hasAuthInfo = useAuthContext()?.authState$ == null;
  const [conf, setConfig] = useAsyncStorage<AppLaunchConfig | null>(
    kAppLaunchConfigKey,
  );
  useEffect(() => {
    void storeService
      .getConfig()
      .then(res => {
        setConfig(res.appLaunch ?? null);
      })
      .catch(noop);
  }, [hasAuthInfo, setConfig, storeService]);
  return conf;
}
